/**
 * Route guard that allows tenant admins OR users with module-specific admin roles.
 * Used for module settings pages that should be accessible to module admins.
 */

import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../shared/auth';

/** Map URL module IDs to permission module names (where they differ) */
const MODULE_ID_TO_PERMISSION: Record<string, string> = {
  'work': 'tasks',
  'ai': 'ai_agents',
  'chat': 'ai_chat',
};

interface ModuleAdminRouteProps {
  children: React.ReactNode;
  /**
   * Explicit permission module name. Use on static settings routes that have no
   * `:moduleId` URL param (e.g. `/procurement/settings`); falls back to the URL
   * param for the canonical `/admin/module-settings/:moduleId` route.
   */
  module?: string;
}

export function ModuleAdminRoute({ children, module }: ModuleAdminRouteProps) {
  const { user } = useAuth();
  const { moduleId } = useParams<{ moduleId: string }>();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Tenant admins always have access
  if (user.is_admin) {
    return <>{children}</>;
  }

  // Check module-specific admin role
  const resolvedModule = module ?? moduleId;
  const permissionModule = MODULE_ID_TO_PERMISSION[resolvedModule || ''] || resolvedModule;
  if (permissionModule && user.module_admin_roles?.includes(permissionModule)) {
    return <>{children}</>;
  }

  return <Navigate to="/" replace />;
}
